import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import {
  listInquiries,
  createInquiry,
  getInquiry,
  type InquiryListItem,
  type InquiryDetail,
} from '../api/inquiry.api';
import styles from './InquiryPage.module.css';

function formatDate(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}

const MAX_LEN = 300;

export default function InquiryPage() {
  const { franchiseId } = useAuth();
  const [list, setList] = useState<InquiryListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isWriteOpen, setIsWriteOpen] = useState(false);
  const [content, setContent] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [detail, setDetail] = useState<InquiryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    if (!franchiseId) return;
    setIsLoading(true);
    setError(null);
    try {
      const rows = await listInquiries(franchiseId);
      setList(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : '목록을 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [franchiseId]);

  useEffect(() => {
    load();
  }, [load]);

  const openWrite = () => {
    setContent('');
    setEmail('');
    setPhone('');
    setFormError(null);
    setIsWriteOpen(true);
  };

  const closeWrite = () => {
    if (isSubmitting) return;
    setIsWriteOpen(false);
  };

  const handleSubmit = async () => {
    if (!franchiseId || isSubmitting) return;
    setFormError(null);

    const trimmed = content.trim();
    if (!trimmed) {
      setFormError('문의 내용을 입력해주세요.');
      return;
    }
    if (trimmed.length > MAX_LEN) {
      setFormError(`문의 내용은 ${MAX_LEN}자 이내로 작성해주세요.`);
      return;
    }

    const emailTrimmed = email.trim();
    const phoneTrimmed = phone.trim().replace(/[-\s]/g, '');
    if (!emailTrimmed && !phoneTrimmed) {
      setFormError('이메일 또는 연락처 중 하나는 반드시 입력해야 합니다.');
      return;
    }
    if (emailTrimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      setFormError('이메일 형식이 올바르지 않습니다.');
      return;
    }
    if (phoneTrimmed && !/^0\d{9,10}$/.test(phoneTrimmed)) {
      setFormError('연락처는 01012345678 형태로 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      await createInquiry(franchiseId, {
        content: trimmed,
        contactEmail: emailTrimmed || undefined,
        contactPhone: phoneTrimmed || undefined,
      });
      setIsWriteOpen(false);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : '작성에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDetail = async (id: string) => {
    if (!franchiseId) return;
    setDetailLoading(true);
    try {
      const d = await getInquiry(franchiseId, id);
      setDetail(d);
    } catch {
      // 무시
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setDetail(null);

  return (
    <Layout title="문의/건의">
      <div className={styles.page}>
        <div className={styles.privacyNote}>
          🔒 모든 문의글은 <b>비공개</b>로 작성됩니다. 본인이 작성한 글과 FAQ(공지)만 열람할 수 있습니다.
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>자주 묻는 질문 & 내 문의</h2>
            <button
              type="button"
              className={styles.writeBtn}
              onClick={openWrite}
              disabled={!franchiseId}
            >
              문의 작성
            </button>
          </div>

          {isLoading && <div className={styles.loadingText}>로딩 중...</div>}
          {error && <div className={styles.errorText}>{error}</div>}

          {!isLoading && !error && list.length === 0 && (
            <div className={styles.empty}>등록된 글이 없습니다.</div>
          )}

          {!isLoading && !error && list.length > 0 && (
            <div className={styles.list}>
              {list.map((item) => (
                <div
                  key={item.id}
                  className={styles.item}
                  onClick={() => openDetail(item.id)}
                >
                  <div className={styles.itemHeader}>
                    {item.isFaq && (
                      <span className={`${styles.badge} ${styles.badgeFaq}`}>FAQ</span>
                    )}
                    {item.isPinned && !item.isFaq && (
                      <span className={`${styles.badge} ${styles.badgePinned}`}>공지</span>
                    )}
                    {item.isMine && (
                      <span className={`${styles.badge} ${styles.badgeMine}`}>내 문의</span>
                    )}
                    {!item.isFaq && (
                      <span className={`${styles.badge} ${styles.badgePrivate}`}>비공개</span>
                    )}
                    <span className={styles.itemAuthor}>{item.maskedAuthor}</span>
                    <span className={styles.itemDate}>{formatDate(item.createdAt)}</span>
                  </div>
                  <div className={styles.itemContent}>{item.content}</div>
                  {item.adminAnswer && (
                    <div className={styles.itemAnswer}>
                      <b>답변: </b>
                      {item.adminAnswer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {isWriteOpen && (
          <div className={styles.overlay} onClick={closeWrite}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h3 className={styles.modalTitle}>문의/건의 작성</h3>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  문의 내용
                  <span className={styles.fieldRequired}>*</span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={content}
                  onChange={(e) => setContent(e.target.value.slice(0, MAX_LEN))}
                  placeholder="문의 또는 건의하실 내용을 작성해주세요. (300자 이내)"
                  maxLength={MAX_LEN}
                  disabled={isSubmitting}
                />
                <div
                  className={`${styles.charCount} ${content.length > MAX_LEN ? styles.charCountOver : ''}`}
                >
                  {content.length} / {MAX_LEN}
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel}>
                  이메일 또는 연락처
                  <span className={styles.fieldRequired}>*</span>
                </label>
                <input
                  className={styles.input}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="이메일 주소 (답변 받으실 주소)"
                  disabled={isSubmitting}
                  autoComplete="email"
                />
                <input
                  className={styles.input}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="연락처 (01012345678)"
                  disabled={isSubmitting}
                  autoComplete="tel"
                  style={{ marginTop: '0.5rem' }}
                />
                <div className={styles.hint}>
                  답변 받으실 수 있도록 이메일 또는 연락처 중 <b>하나 이상</b>을 입력해주세요.
                </div>
              </div>

              {formError && <div className={styles.errorText}>{formError}</div>}

              <div className={styles.btnRow}>
                <button
                  type="button"
                  className={styles.btnCancel}
                  onClick={closeWrite}
                  disabled={isSubmitting}
                >
                  취소
                </button>
                <button
                  type="button"
                  className={styles.btnSubmit}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '등록 중...' : '등록'}
                </button>
              </div>
            </div>
          </div>
        )}

        {(detail || detailLoading) && (
          <div className={styles.overlay} onClick={closeDetail}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              {detailLoading && <div className={styles.loadingText}>로딩 중...</div>}
              {detail && (
                <>
                  <div className={styles.detailHeader}>
                    {detail.isFaq && (
                      <span className={`${styles.badge} ${styles.badgeFaq}`}>FAQ</span>
                    )}
                    {detail.isMine && (
                      <span className={`${styles.badge} ${styles.badgeMine}`}>내 문의</span>
                    )}
                    {!detail.isFaq && (
                      <span className={`${styles.badge} ${styles.badgePrivate}`}>비공개</span>
                    )}
                    <span className={styles.itemAuthor}>{detail.maskedAuthor}</span>
                    <span className={styles.itemDate}>{formatDate(detail.createdAt)}</span>
                  </div>

                  {detail.isMine && (detail.contactEmail || detail.contactPhone) && (
                    <div className={styles.detailContact}>
                      답변 받을 연락처:{' '}
                      {detail.contactEmail && <span>{detail.contactEmail}</span>}
                      {detail.contactEmail && detail.contactPhone && ' / '}
                      {detail.contactPhone && <span>{detail.contactPhone}</span>}
                    </div>
                  )}

                  <div className={styles.detailContent}>{detail.content}</div>

                  {detail.adminAnswer && (
                    <>
                      <div className={styles.detailAnswerTitle}>답변</div>
                      <div className={styles.itemAnswer}>{detail.adminAnswer}</div>
                    </>
                  )}

                  <div className={styles.btnRow}>
                    <button
                      type="button"
                      className={styles.btnCancel}
                      onClick={closeDetail}
                      style={{ flex: 'none', minWidth: '100px', marginLeft: 'auto' }}
                    >
                      닫기
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
